import { AnimatePresence, motion } from 'framer-motion'
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Button } from './Button'
import { IconButton } from './IconButton'
import { BottomSheet } from './BottomSheet'
import { usePushBackHandler } from '../../context/BackStackContext'
import { useIsMobile } from './useIsMobile'
import { getNlsString } from '../../nls/nlsRuntime'

/* ---------------------------------------------------------------------------
 * Public types
 * ---------------------------------------------------------------------------*/

export type AppDialogActionVariant = 'primary' | 'danger' | 'ghost' | 'secondary'

export interface AppDialogAction {
  label: string
  variant?: AppDialogActionVariant
  onClick: () => void | Promise<void>
  autoFocus?: boolean
  disabled?: boolean
  loading?: boolean
  testId?: string
}

/** Sizes added in 1.g.a. 'default' stays as an alias of 'sm'. */
type AppDialogSize =
  | 'default'
  | 'sm'
  | 'md'
  | 'lg'
  | 'full'
  | 'analysisChart'
  | 'workspaceContextPicker'
  | 'attachPicker'

interface AppDialogProps {
  open: boolean
  title: string
  message?: ReactNode
  actions: AppDialogAction[]
  /** Fired on overlay click / Escape. Usually maps to the cancel action. */
  onDismiss?: () => void
  size?: AppDialogSize
  'data-testid'?: string
  dialogRef?: RefObject<HTMLDivElement | null>
  /** Optional icon node displayed in `.appDialogIcon` beside the title. */
  icon?: ReactNode
  /** Tint for the icon wrapper. Default: 'default' (accent). */
  tone?: 'default' | 'danger'
  /** Extra node displayed in `.appDialogHead` right after the title. */
  headerExtra?: ReactNode
  /** Extra body content rendered in `.appDialogBody` after message. */
  children?: ReactNode
  /**
   * Whether to show the close (×) button.
   * Default: true for md/lg/full when onDismiss exists; false for sm/default.
   */
  showClose?: boolean
}

/* ---------------------------------------------------------------------------
 * Module-level Esc/Tab stack
 * Each entry: { dismiss, cardRef }
 * Only the topmost entry handles Esc and Tab.
 * ---------------------------------------------------------------------------*/

type StackEntry = {
  dismiss: () => void
  cardRef: RefObject<HTMLElement | null>
}

const dialogStack: StackEntry[] = []

function pushDialog(entry: StackEntry) {
  dialogStack.push(entry)
}

function popDialog(entry: StackEntry) {
  const idx = dialogStack.indexOf(entry)
  if (idx !== -1) dialogStack.splice(idx, 1)
}

function isTopOfStack(entry: StackEntry) {
  return dialogStack.length > 0 && dialogStack[dialogStack.length - 1] === entry
}

/* ---------------------------------------------------------------------------
 * Focusable selector
 * ---------------------------------------------------------------------------*/

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE))
}

/* ---------------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------------------*/

import { DialogDepthContext } from './dialogDepthContext'

/**
 * The single, reusable app modal. All future confirmation/choice dialogs should
 * route through this component so the look stays consistent across the app.
 * Pairs with the `.appDialog*` rules in 04-chat-manage-onboarding.css.
 */
export const AppDialog = ({
  open,
  title,
  message,
  actions,
  onDismiss,
  size = 'default',
  'data-testid': testId,
  dialogRef,
  icon,
  tone = 'default',
  headerExtra,
  children,
  showClose,
}: AppDialogProps) => {
  const depth = useContext(DialogDepthContext)
  const [pendingActionIndex, setPendingActionIndex] = useState<number | null>(null)
  const dialogBusy = pendingActionIndex !== null || actions.some((action) => action.loading)

  const dismiss = useCallback(() => {
    if (!dialogBusy) onDismiss?.()
  }, [dialogBusy, onDismiss])

  usePushBackHandler(dismiss, open && Boolean(onDismiss) && !dialogBusy)

  const isMobile = useIsMobile()

  // Internal card ref (used when dialogRef is not provided)
  const internalCardRef = useRef<HTMLDivElement | null>(null)
  const cardRef = (dialogRef ?? internalCardRef) as RefObject<HTMLElement | null>

  // Track the element focused before opening so we can restore it on close.
  const lastFocusRef = useRef<HTMLElement | null>(null)

  // Stack entry for Esc/Tab top-of-stack logic
  const stackEntryRef = useRef<StackEntry>({ dismiss, cardRef })
  // Keep dismiss and cardRef in entry current
  useEffect(() => {
    stackEntryRef.current.dismiss = dismiss
    stackEntryRef.current.cardRef = cardRef
  }, [dismiss, cardRef])

  // Register/unregister from the module-level stack
  useEffect(() => {
    if (!open) return
    // Capture opener element
    lastFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    const entry = stackEntryRef.current
    pushDialog(entry)

    // Focus first autoFocus action, else first focusable, else card
    const focusTimer = window.setTimeout(() => {
      const card = cardRef.current
      if (!card) return
      const autoFocusEl = card.querySelector<HTMLElement>('[autofocus]')
      const firstFocusable = getFocusableElements(card)[0]
      if (autoFocusEl) autoFocusEl.focus()
      else if (firstFocusable) firstFocusable.focus()
      else card.focus()
    }, 20)

    return () => {
      window.clearTimeout(focusTimer)
      popDialog(entry)
      // Restore focus to opener
      lastFocusRef.current?.focus?.()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Global keydown — Esc and Tab, only for top of stack
  useEffect(() => {
    if (!open) return

    const handleKey = (event: KeyboardEvent) => {
      if (!isTopOfStack(stackEntryRef.current)) return

      if (event.key === 'Escape') {
        stackEntryRef.current.dismiss()
        return
      }

      if (event.key === 'Tab') {
        const card = stackEntryRef.current.cardRef.current
        if (!card) return
        const focusable = getFocusableElements(card)
        if (focusable.length === 0) return

        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  const [prevOpen, setPrevOpen] = useState(open)
  if (!open && prevOpen) {
    setPrevOpen(false)
    setPendingActionIndex(null)
  } else if (open && !prevOpen) {
    setPrevOpen(true)
  }

  const runAction = (action: AppDialogAction, index: number) => {
    if (dialogBusy || action.disabled || action.loading) return
    const result = action.onClick()
    if (result && typeof result.then === 'function') {
      setPendingActionIndex(index)
      void result.then(
        () => {
          setPendingActionIndex((current) => (current === index ? null : current))
        },
        () => {
          setPendingActionIndex((current) => (current === index ? null : current))
        },
      )
    }
  }

  const isCustomSized =
    size === 'analysisChart' || size === 'workspaceContextPicker' || size === 'attachPicker'

  // Compute effective class name — 'default' always gets --default; 'sm' gets both --sm
  // (and --default as well so existing CSS keeps applying).
  const cardSizeClass =
    size === 'sm'
      ? 'appDialogCard--sm appDialogCard--default'
      : `appDialogCard--${size}`

  // Close button visibility
  const isLargeSize = size === 'md' || size === 'lg' || size === 'full'
  const showCloseButton =
    showClose !== undefined
      ? showClose
      : isLargeSize && Boolean(onDismiss)

  // Action buttons JSX — shared between card and sheet footer
  const actionsJsx =
    !isCustomSized && actions.length > 0 ? (
      <div className="appDialogActions">
        {actions.map((action, index) => {
          const loading = action.loading || pendingActionIndex === index
          const variant =
            action.variant === 'secondary'
              ? 'secondary'
              : action.variant === 'danger'
                ? 'danger'
                : action.variant === 'primary'
                  ? 'primary'
                  : 'tertiary'
          return (
            <Button
              key={action.label || `action-${index}`}
              variant={variant}
              size="md"
              autoFocus={action.autoFocus}
              disabled={dialogBusy || action.disabled}
              loading={loading}
              data-testid={action.testId}
              aria-busy={loading || undefined}
              onClick={() => runAction(action, index)}
            >
              {action.label}
            </Button>
          )
        })}
      </div>
    ) : null

  // Mobile: md/lg/full → BottomSheet; sm/default/legacy → card
  if (isMobile && isLargeSize) {
    return (
      <DialogDepthContext.Provider value={depth + 1}>
        <BottomSheet
          isOpen={open}
          onClose={dismiss}
          dismissible={Boolean(onDismiss) && !dialogBusy}
          title={title}
          footer={actionsJsx}
        >
          {message && <div className="appDialogMessage">{message}</div>}
          {children && <div className="appDialogBody">{children}</div>}
        </BottomSheet>
      </DialogDepthContext.Provider>
    )
  }

  // Head layout: with icon vs without
  const headJsx = !isCustomSized ? (
    <div className="appDialogHead">
      {icon ? (
        <>
          <span
            className={[
              'appDialogIcon',
              tone === 'danger' ? 'appDialogIcon--danger' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {icon}
          </span>
          <div style={{ flex: 1 }}>
            <h3 className="appDialogTitle">{title}</h3>
            {message && <div className="appDialogMessage">{message}</div>}
          </div>
        </>
      ) : (
        <>
          <h3 className="appDialogTitle" style={{ flex: 1 }}>
            {title}
          </h3>
          {headerExtra && <div className="appDialogHeadExtra">{headerExtra}</div>}
          {showCloseButton && (
            <IconButton
              size="sm"
              variant="tertiary"
              className="appDialogClose"
              label={getNlsString('NLSCommonActionClose')}
              onClick={dismiss}
              icon={<CloseRoundedIcon fontSize="inherit" />}
            />
          )}
        </>
      )}
    </div>
  ) : null

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="appDialogOverlay"
          data-dialog-depth={depth > 0 ? depth : undefined}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          onClick={dismiss}
        >
          <DialogDepthContext.Provider value={depth + 1}>
            <motion.div
              ref={(dialogRef ?? internalCardRef) as RefObject<HTMLDivElement | null>}
              className={`appDialogCard ${cardSizeClass}`}
              role="dialog"
              aria-modal="true"
              aria-label={title}
              data-testid={testId}
              tabIndex={-1}
              initial={{ opacity: 0, y: 12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.97 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              onClick={(event) => event.stopPropagation()}
            >
              {headJsx}
              {isCustomSized && message && <div className="appDialogMessage">{message}</div>}
              {/* For regular sizes without icon, render message below head */}
              {!icon && !isCustomSized && message && (
                <div className="appDialogMessage">{message}</div>
              )}
              {children && <div className="appDialogBody">{children}</div>}
              {actionsJsx}
            </motion.div>
          </DialogDepthContext.Provider>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
